import { Button, Card } from "@rumbledirect/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { message, Modal, theme, Typography } from "antd";
import { useState } from "react";
import { MIME_TYPE_MERGE_PATCH_JSON } from "@rumble-it/common-lib/src/constants/mimeTypes";
import {
  useViewConfigurationAffiliateId,
  useViewConfigurationClient,
} from "../client";

export interface LocationOption {
  "@id": string;
  "@type": "ServicePartner" | "Company";
  id: string;
  name: string;
  fullName?: string;
  address?: {
    street?: string;
    streetNumber?: string;
    streetNumberAddition?: string;
    postalCode?: string;
    city?: string;
  };
}

export interface LocationSelectorCardProps {
  title: string;
  emptyState: string;
  submitEndpoint: string;
  submitField: string;
  selectedName: string;
  selectedAddress: string;
}

const formatAddress = (address?: LocationOption["address"]) => {
  if (!address) return "";
  return [
    [address.street, address.streetNumber, address.streetNumberAddition]
      .filter(Boolean)
      .join(" "),
    [address.postalCode, address.city].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");
};

export const LocationSelectorCard = ({
  title,
  emptyState,
  submitEndpoint,
  submitField,
  selectedName,
  selectedAddress,
}: LocationSelectorCardProps) => {
  const axiosInstance = useViewConfigurationClient();
  const queryClient = useQueryClient();
  const [optionsVisible, setOptionsVisible] = useState(false);
  const { token } = theme.useToken();

  const affiliateId = useViewConfigurationAffiliateId();

  const servicePartnersQuery = useQuery({
    queryKey: ["servicePartners", affiliateId],
    queryFn: async (): Promise<LocationOption[]> =>
      axiosInstance.get("/service-partners").then((response) => response.data),
    enabled: optionsVisible,
  });

  const submitMutation = useMutation({
    mutationFn: async (option: LocationOption) =>
      axiosInstance.patch(
        submitEndpoint,
        { [submitField]: option["@id"] },
        { headers: { "Content-Type": MIME_TYPE_MERGE_PATCH_JSON } },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["view-configuration"] });
      setOptionsVisible(false);
    },
    onError: () => {
      message.error("Er is een fout opgetreden. Probeer het opnieuw.");
    },
  });

  const options = servicePartnersQuery.data ?? [];

  const isLoading = servicePartnersQuery.isLoading;

  return (
    <Card
      title={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>{title}</span>
          <Button
            type="link"
            style={{ padding: 0, fontWeight: "normal" }}
            onClick={() => setOptionsVisible(true)}
          >
            wijzigen
          </Button>
        </div>
      }
    >
      {selectedName ? (
        <div>
          <Typography.Text strong>{selectedName}</Typography.Text>
          {selectedAddress && (
            <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
              {selectedAddress}
            </Typography.Paragraph>
          )}
        </div>
      ) : (
        <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
          {emptyState}
        </Typography.Paragraph>
      )}

      <Modal
        title={`${title} wijzigen`}
        open={optionsVisible}
        onCancel={() => setOptionsVisible(false)}
        footer={null}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 12,
            maxHeight: "60vh",
            overflowY: "auto",
          }}
        >
          {isLoading && <Card loading />}
          {!isLoading &&
            options.map((option) => {
              const isSelected =
                (option.name || option.fullName) === selectedName;
              const isOptionPending =
                submitMutation.isPending &&
                submitMutation.variables?.["@id"] === option["@id"];

              return (
                <div
                  key={option["@id"]}
                  style={{
                    border: isSelected
                      ? `1px solid ${token.colorPrimary}`
                      : `1px solid ${token.colorBorderSecondary}`,
                    borderRadius: 8,
                    padding: 16,
                  }}
                >
                  <Typography.Text strong>
                    {option.name || option.fullName}
                  </Typography.Text>
                  <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
                    {formatAddress(option.address)}
                  </Typography.Paragraph>
                  <Button
                    type={isSelected ? "primary" : "default"}
                    style={{ marginTop: 8, width: "100%" }}
                    loading={isOptionPending}
                    disabled={submitMutation.isPending && !isOptionPending}
                    onClick={() => submitMutation.mutate(option)}
                  >
                    Hersteller kiezen
                  </Button>
                </div>
              );
            })}
        </div>
      </Modal>
    </Card>
  );
};
